import { Fragment, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { abortController } from '../../utils/abortController';
import useSessionExpire from '../../hooks/useSessionExpire';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import CustomTextMask from '../../components/CustomTextMask';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { isFieldLocked } from '../../utils/fieldUtils';
import { InputAdornment, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';


const DealSBLC = ({ isWritable = false, isListDataLoading = false, isPFIAccordionExpanded = null, isProductGradeAccordionExpanded = null, selectedTab = null, selectedPFI = null, currentTranche = null, sblcFormData = {}, tabName = null, subTabName = null, isPeriodLocked = false, lockedFields = [], pfiAccordionRefs = [], productGradeAccordionRefs = [], setIsPFIAccordionExpanded = () => {}, setIsProductGradeAccordionExpanded = () => {}, setFormData = () => {}, setSBLCFormData = () => {}, setShowAlert = () => {}, setAlertMessage = () => {}, setAlertType = () => {}, logOut = () => {} }) => 
{
  const location = useLocation();
  const { selectedBusinessUnit } = location.state || {};

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* LOADING VARIABLES */
  const [isSBLCListLoading, setIsSBLCListLoading] = useState(false);
  const [isPFIListLoading, setIsPFIListLoading] = useState(false);
  const [isSelectedPFIDataLoading, setIsSelectedPFIDataLoading] = useState(false);

/* ACCORDION VARIABLES */
  const prevPFICountRef = useRef(sblcFormData?.PFI_Id?.length || 0);
  const prevProductGradeCountRef = useRef(
    sblcFormData?.PFI_Id?.at(-1)?.Product_Grade_Id?.length || 0
  );

  /* LIST VARIABLES */
  const [sblcs, setSBLCs] = useState([]);

  /* DIALOG VARIABLES */
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  useEffect(() =>
  {
    const fetchSBLCList = async () =>
    {
      setIsSBLCListLoading(true);

      await axios({
        method: "post",
        url: "/api/GetSBLCList",
        data: {
          business_unit_id: selectedBusinessUnit?.id,
          selected_tab: selectedTab,
          Deal_PFI_Id: selectedPFI
        },
        signal: abortController.signal
      })
      .then((response) => 
      {
        setIsSBLCListLoading(false);
        const { status, data } = response;

        if (status === 200) 
        {
          const sblcs = data?.sblc || [];
          setSBLCs(sblcs);
        }
      })
      .catch((error) => 
      {
        console.log("Get SBLC Api: ", error);
        setIsSBLCListLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
        {
          return;
        }

        const status = error?.response?.status;

        if (status === 403) 
        {
          triggerSessionExpire();
        } 
        else 
        {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
              ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
              : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);
      
          if (status === 429) 
          {
            setTimeout(logOut, 3000);
          }
        }
      });
    }

    fetchSBLCList();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedTab, selectedPFI]);

  useEffect(() => 
  { 
    const setSBLCData = async () => 
    {
      const initialSBLC = {
        ...sblcFormData,
        SBLC_Id: sblcs?.[0] || null,
        PFI_Id: [],
        PFIs: []
      };
      setSBLCFormData(initialSBLC);
  
      if (initialSBLC.SBLC_Id?.id) 
      {
        const pfis = await fetchPFIList(initialSBLC.SBLC_Id.id);
      
        setSBLCFormData((previousSBLCFormData) => ({
          ...previousSBLCFormData,
          PFIs: pfis
        }));
      }
    };
  
    if (selectedTab === "Shipment Details" && !sblcFormData.SBLC_Id) 
    {
      setSBLCData();
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedTab, currentTranche, sblcs]);

  useEffect(() => 
  {
    setFormData((previousFormData) => 
    {
      let updatedTotalAmount = sblcFormData?.PFI_Id?.reduce(
        (acc, pfi) => acc + (pfi.Product_Grade_Id?.reduce(
          (gradeAccumulator, grade) => 
            gradeAccumulator + (parseFloat(grade.Quantity || 0) * parseFloat(grade.Selling_Price || 0)),
          0)
        ),
        0
      ) || 0;
      // Round Total_Amount to 3 decimal places if it has more
      if ((updatedTotalAmount.toString().split(".")[1]?.length || 0) > 3)
      {
        updatedTotalAmount = parseFloat(updatedTotalAmount.toFixed(3));
      }
  
      return {
        ...previousFormData,
        Total_Amount: updatedTotalAmount,
        Freight: previousFormData.Total_Amount !== updatedTotalAmount ? null : previousFormData.Freight,
        FOB_Value: previousFormData.Total_Amount !== updatedTotalAmount ? null : previousFormData.FOB_Value
      };
    });

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [sblcFormData]);



  useEffect(() => {
    const currentPFICount = sblcFormData?.PFI_Id?.length || 0;
    const prevPFICount = prevPFICountRef.current;
    const lastPFI = sblcFormData?.PFI_Id?.at(-1);
    const currentProductGradeCount = lastPFI?.Product_Grade_Id?.length || 0;
    const prevProductGradeCount = prevProductGradeCountRef.current;

    // Only expand PFI accordion if a new PFI is added
    if (currentPFICount > prevPFICount) {
      setIsPFIAccordionExpanded(currentPFICount - 1);
    }

    // Only expand Product Grade accordion if new grades are added to the last PFI
    if (currentProductGradeCount > prevProductGradeCount) {
      setIsProductGradeAccordionExpanded(currentProductGradeCount - 1);
    }

    // Update previous counts
    prevPFICountRef.current = currentPFICount;
    prevProductGradeCountRef.current = currentProductGradeCount;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sblcFormData?.PFI_Id]);
    
  const fetchPFIList = async (sblcId) => 
  {
    try
    {
      setIsPFIListLoading(true);

      const response = await axios({
        method: "post",
        url: "/api/GetSBLCPFIList",
        data: { 
          pathname: location?.pathname,
          sblc_id: sblcId,
          selected_tab: selectedTab
        },
        signal: abortController.signal
      });

      setIsPFIListLoading(false);
      const { status, data } = response;

      if (status === 200) 
      {
        const pfis = data?.pfis || [];
        return pfis;
      }
      else if (status === 202)
      {
        setAlertMessage(data?.error || "");
        setAlertType("info");
        setShowAlert(true);
      }
    }
    catch (error)
    { 
      console.log("Get Selected SBLC PFIs Api: ", error);
      setIsPFIListLoading(false);

      const status = error?.response?.status;

      if (status === 403) 
      {
        triggerSessionExpire();
      } 
      else 
      {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
            : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);
    
        if (status === 429) 
        {
          setTimeout(logOut, 3000);
        }
      }
    }
  }

  const fetchSelectedPFIData = async (sblcId, pfiId) =>
  {
    try
    {
      setIsSelectedPFIDataLoading(true);

      const response = await axios({
        method: "post",
        url: "/api/GetSelectedSBLCPFIData",
        data: { 
          Deal_PFI_Id: selectedPFI,
          sblc_id: sblcId,
          pfi_id: pfiId,
          selected_tab: selectedTab
        },
        signal: abortController.signal
      });

      setIsSelectedPFIDataLoading(false);
      const { status, data } = response;

      if (status === 200) 
      {
        const pfiData = data?.pfi_data || {};
        return pfiData;
      }
      else if (status === 202)
      {
        setAlertMessage(data?.error || "");
        setAlertType("info");
        setShowAlert(true);
      }
    }
    catch (error) 
    {
      console.log("Get Selected PFI Data Api: ", error);
      setIsSelectedPFIDataLoading(false);

      const status = error?.response?.status;

      if (status === 403) 
      {
        triggerSessionExpire();
      } 
      else 
      {
        setAlertMessage(
          status === 401
            ? "Unauthorized access. You do not have the required permissions to perform this action."
            : status === 429
            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
            : "An error occurred while processing your request. Please try again later or contact the site administrator."
        );
        setAlertType("error");
        setShowAlert(true);
    
        if (status === 429) 
        {
          setTimeout(logOut, 3000);
        }
      }
    }
  }
  
  const handleAccordionChange = (index, flag) => (_, newExpanded) => 
  {
    if (flag === "PFI") 
    {
      setIsPFIAccordionExpanded(newExpanded ? index : null);
    }
    else if (flag === "Product Grade") 
    {
      setIsProductGradeAccordionExpanded(newExpanded ? index : null);
    } 
  }

  const handleSelectSBLC = async (newValue) =>
  {
    const sblc = { ...sblcFormData };

    sblc.SBLC_Id = newValue;
    sblc.PFI_Id = [];
    sblc.isUpdated = true;

    if (newValue)
    {
      const pfis = await fetchPFIList(sblc.SBLC_Id.id) || [];
      sblc.PFIs = pfis;
    }
    else
    {
      sblc.PFIs = [];
    }

    setSBLCFormData(sblc);
    setIsPFIAccordionExpanded(null);
    setIsProductGradeAccordionExpanded(null);
  }

  const handleAddWrapper = (type, pfiIndex = null) => 
  {
    if (type === "PFI") 
    {
      handleAddPFI();
    } 
    else if (type === "Product Grade") 
    {
      handleAddProductGrade(pfiIndex);
    }
  }

  const handleSelectPFI = async (pfiIndex, newValue) => 
  {
    const pfis = [...(sblcFormData?.PFI_Id || [])];

    if (!pfis[pfiIndex]?.isNew) 
    {
      pfis[pfiIndex].isUpdated = true;
    }

    pfis[pfiIndex].PFI_Number = newValue;
    pfis[pfiIndex].Product_Grade_Id = [];

    if (newValue)
    {
      const pfiData = await fetchSelectedPFIData(sblcFormData?.SBLC_Id?.id, newValue.id) || {};
      const { Product_Id: productId = null, Product_Name: productName = null, Shipment_Instruction_Id: siId = null, Product_Grades: productGrades = [] } = pfiData;
      const siNumber = `${newValue.value}/SI${siId?.toString()?.padStart(3, '0')?.slice(-3)}`;

      pfis[pfiIndex].Product_Id = { id: productId, value: productName };
      pfis[pfiIndex].SI_Id = { id: siId, value: siNumber };
      pfis[pfiIndex].Product_Grades = productGrades;
    }
    else
    {
      pfis[pfiIndex].Product_Id = null;
      pfis[pfiIndex].SI_Id = null;
      pfis[pfiIndex].Product_Grades = [];
    }

    setSBLCFormData((previousSBLCFormData) => ({
      ...previousSBLCFormData,
      PFI_Id: pfis
    }));
  }
  
  const handleAddPFI = () => 
  {
    const pfis = [...(sblcFormData?.PFI_Id || [])];
    const isAnyPFIFieldFilled = pfis?.length > 0 
      ? pfis.every((pfi) => 
        Object.entries(pfi)
          .filter(([key]) => key !== "isNew" && key !== "isUpdated")
          .some(([_, value]) => 
          {
            if (typeof value === "object" && value !== null) 
            {
              return Array.isArray(value)
                ? value.length > 0
                : Object.values(value).some(subValue => 
                    subValue !== undefined && subValue !== "" && subValue !== null &&
                    (typeof subValue !== "object" || (Array.isArray(subValue) ? subValue.length > 0 : true))
                  );
            }

            return value !== undefined && value !== "" && value !== null &&
              (typeof value !== "object" || (Array.isArray(value) ? value.length > 0 : true));
          })
      )
    : true;

    if (isAnyPFIFieldFilled)
    {
      const newPFIData = {
        PFI_Number: null,
        SI_Id: null,
        Product_Id: null,
        Product_Grades: [],
        Product_Grade_Id: [],
        isNew: true,
        isUpdated: false
      };

      setSBLCFormData((previousSBLCFormData) => ({
        ...previousSBLCFormData,
        PFI_Id: [...pfis, newPFIData]
      }));
    }
    else
    {
      setShowWarningDialog(true);
    }
  }

  const handleDeletePFI = (event, pfiIndex) =>
  {
    event.stopPropagation();
    const pfis = [...(sblcFormData?.PFI_Id || [])];

    setSBLCFormData((previousSBLCFormData) => ({
      ...previousSBLCFormData,
      PFI_Id: pfis.filter((_, index) => index !== pfiIndex)
    }));
  }

  const handleSelectProductGrade = async (pfiIndex, pfiProductGradeIndex, newValue) => 
  {
    const pfis = [...(sblcFormData?.PFI_Id || [])];
    const pfiProductGrades = [...(pfis[pfiIndex]?.Product_Grade_Id || [])];

    if (!pfiProductGrades[pfiProductGradeIndex]?.isNew) 
    {
      pfiProductGrades[pfiProductGradeIndex].isUpdated = true;
    }

    pfiProductGrades[pfiProductGradeIndex].Grade_Id = newValue;

    if (selectedTab === "Deal Header")
    {
      pfiProductGrades[pfiProductGradeIndex].Quantity = newValue?.Quantity;
      pfiProductGrades[pfiProductGradeIndex].Buying_Price = newValue?.Buying_Price;
    }

    pfis[pfiIndex].Product_Grade_Id = pfiProductGrades;

    setSBLCFormData((previousSBLCFormData) => ({
      ...previousSBLCFormData,
      PFI_Id: pfis
    }));
  }

  const handleProductGradeChange = (pfiIndex, pfiProductGradeIndex, field, value) => 
  {
    const pfis = structuredClone(sblcFormData?.PFI_Id || []);
    const pfiProductGrades = structuredClone(pfis[pfiIndex]?.Product_Grade_Id || []);
    let formattedValue = value;

    if (field === "HS_Code") 
    {
      const hsCode = pfiProductGrades[pfiProductGradeIndex]?.HS_Code || null;
    
      if (hsCode === formattedValue) 
      {
        return;
      }
    }

    if (["Quantity", "Buying_Price", "Selling_Price"].includes(field)) 
    {
      formattedValue = value.replace(/[^0-9.]/g, "");
      if (formattedValue.includes(".")) 
      {
        const [integerPart, decimalPart] = formattedValue.split(".");
        if (field === "Quantity")
        {
          formattedValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
        }
        else if (["Buying_Price", "Selling_Price"].includes(field))
        {
          // Ensure the value has at most 3 decimal places, round if more
          if (decimalPart.length > 3)
          {
            formattedValue = parseFloat(`${integerPart}.${decimalPart}`).toFixed(3);
          }
          else
          {
            formattedValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
          }
        }
      }
    }

    // Ensure immutability by creating a new object
    const updatedProductGrade = {
      ...pfiProductGrades[pfiProductGradeIndex],
      [field]: formattedValue,
      ...(pfiProductGrades[pfiProductGradeIndex]?.isNew ? {} : { isUpdated: true })
    };

    const updatedProductGrades = [...pfiProductGrades];
    updatedProductGrades[pfiProductGradeIndex] = updatedProductGrade;

    const updatedPfis = [...pfis];
    updatedPfis[pfiIndex] = {
      ...pfis[pfiIndex],
      Product_Grade_Id: updatedProductGrades
    };

    setSBLCFormData((previousSBLCFormData) => ({
      ...previousSBLCFormData,
      PFI_Id: updatedPfis
    }));
  }  

  const handleAddProductGrade = (pfiIndex) => 
  {
    const pfis = [...(sblcFormData?.PFI_Id || [])];
    const pfiProductGrades = [...(pfis[pfiIndex]?.Product_Grade_Id || [])];
    const isAnyProductGradeFieldFilled = pfiProductGrades?.length > 0 
      ? pfiProductGrades.every((pfiProductGrade) => 
        Object.entries(pfiProductGrade)
          .filter(([key]) => key !== "isNew" && key !== "isUpdated")
          .some(([_, value]) => 
          {
            if (typeof value === "object" && value !== null) 
            {
              return Array.isArray(value)
                ? value.length > 0
                : Object.values(value).some(subValue => 
                    subValue !== undefined && subValue !== "" && subValue !== null &&
                    (typeof subValue !== "object" || (Array.isArray(subValue) ? subValue.length > 0 : true))
                  );
            }

            return value !== undefined && value !== "" && value !== null &&
              (typeof value !== "object" || (Array.isArray(value) ? value.length > 0 : true));
          })
      )
    : true;

    if (isAnyProductGradeFieldFilled)
    {
      const newProductGradeData = {
        Grade_Id: null,
        Quantity: null,
        Selling_Price: null,
        Buying_Price: null,
        HS_Code: null,
        Specification: "",
        Description: "",
        isNew: true,
        isUpdated: false
      };
      
      pfis[pfiIndex].Product_Grade_Id = [...pfiProductGrades, newProductGradeData];
      
      setIsProductGradeAccordionExpanded(pfiProductGrades?.length);
      setSBLCFormData((previousSBLCFormData) => ({
        ...previousSBLCFormData,
        PFI_Id: pfis
      }));
    }
    else
    {
      setShowWarningDialog(true);
    }
  }

  const handleDeleteProductGrade = (event, pfiIndex, pfiProductGradeIndex) =>
  {
    event.stopPropagation();

    const pfis = [...(sblcFormData?.PFI_Id || [])];
    const pfiProductGrades = [...(pfis[pfiIndex]?.Product_Grade_Id || [])];

    pfiProductGrades?.splice(pfiProductGradeIndex, 1);
    pfis[pfiIndex].Product_Grade_Id = pfiProductGrades;

    setIsProductGradeAccordionExpanded(pfiProductGrades?.length - 1);
    setSBLCFormData((previousSBLCFormData) => ({
      ...previousSBLCFormData,
      PFI_Id: pfis
    }));
  }

  const handleCloseDialog = () => 
  {
    setShowWarningDialog(false);
  }

  return (
    <Grid size = {{ xs: 12 }} className = "mb-1">
      <Grid size = {{ xs: selectedTab === "Deal Header" ? 6 : 4 }} className = "form-text-field mb-2">
        {selectedTab === "Shipment Details" ? (
          <TextField
            label = "SBLC"
            id = "SBLC_Id"
            value = {sblcFormData?.SBLC_Id?.value || ""}
            disabled
            slotProps = {{
              inputLabel: { 
                shrink: sblcFormData?.SBLC_Id ? true : false
              },
              input: {
                endAdornment: (
                  <Fragment>
                    {isSBLCListLoading ? 
                      <CircularProgress 
                        color = "inherit" 
                        size = {20} 
                        sx = {{  
                          marginRight: '1em'
                        }}
                      /> 
                    : null}
                  </Fragment>
                )
              }
            }}
          />
        ) : (
          <VirtualizedAutocomplete
            isLoading = {isListDataLoading || isPFIListLoading}
            isMultiple = {false}
            isObject = {true}
            isRequired = {true}
            isDisabled = {isPFIListLoading || (Boolean(isPeriodLocked && isFieldLocked("SBLC_Id", subTabName, tabName, lockedFields)))}
            isWritable = {isWritable}
            filterOn = "SBLC"
            options = {sblcs}
            selectedOptions = {sblcFormData?.SBLC_Id || null}
            handleSelectChange = {(filterOn, newValue) => handleSelectSBLC(newValue)}
          />
        )}
      </Grid>
      
      {sblcFormData?.PFIs?.length > 0 ? (
        <Grid size = {{ xs: 12 }} className = "mt-2">
          <div className = "w-100 d-flex justify-content-between align-items-center mb-2">
            <h4 className = "sub-heading mb-0">Proforma Invoices</h4>
            
            <IconButton
              color = "primary"
              disabled = {!isWritable || isPeriodLocked}
              onClick = {() => handleAddWrapper("PFI")}
            >
              <AddBoxIcon 
                fontSize = "large"
                disabled = {isPeriodLocked}
                sx = {{ color: '#0C969C' }}
              />
            </IconButton>
          </div>

          {sblcFormData?.PFI_Id?.map((pfi, pfiIndex) => {
            // Create a Set of ids for PFIs for fast lookup
            const pfiIdSet = new Set(sblcFormData?.PFI_Id?.map(sblcPFI => sblcPFI?.PFI_Number?.id) || []);
            const pfiValue = pfi?.PFI_Number?.value || `PFI ${pfiIndex + 1}`;

            return (
              <Grid key = {pfiIndex}>
                <Accordion
                  ref = {(element) => (pfiAccordionRefs.current[pfiIndex] = element)}
                  expanded = {isPFIAccordionExpanded === pfiIndex}
                  component = "div"
                  onChange = {handleAccordionChange(pfiIndex, "PFI")}
                >
                  <AccordionSummary
                    expandIcon = {<ExpandMoreIcon />}
                    aria-controls = {`pfi-content-${pfiIndex}`}
                    id = {`pfi-header-${pfiIndex}`}
                  >
                    <small className = "page-heading fw-bold item">
                      {pfiValue}
                    </small>
                    
                    <IconButton
                      color = "error"
                      size = "small"
                      className = "pt-0"
                      disabled = {!isWritable || isPeriodLocked}
                      onClick = {(event) => handleDeletePFI(event, pfiIndex)}
                    >
                      <DeleteIcon fontSize = "medium" />
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing = {2} className = "w-100">
                      <Grid size = {{ xs: selectedTab === "Deal Header" ? 6 : 3 }}>
                        <VirtualizedAutocomplete
                          isLoading = {isSelectedPFIDataLoading}
                          isMultiple = {false}
                          isObject = {true}
                          isRequired = {true}
                          isDisabled = {isSelectedPFIDataLoading || (Boolean(isPeriodLocked && isFieldLocked("Seller_Id", subTabName, tabName, lockedFields)))}
                          isWritable = {isWritable}
                          filterOn = "PFI"
                          options = {sblcFormData?.PFIs?.filter(sblcPFI => !pfiIdSet.has(sblcPFI.id)) || []}
                          selectedOptions = {pfi?.PFI_Number || null}
                          handleSelectChange = {(filterOn, newValue) => handleSelectPFI(pfiIndex, newValue)}
                        />
                      </Grid>

                      <Grid size = {{ xs: selectedTab === "Deal Header" ? 6 : 3 }} className = "form-text-field">
                        <TextField
                          label = "Product"
                          id = "Product_Id"
                          value = {pfi?.Product_Id?.value || ""}
                          disabled
                          slotProps = {{
                            inputLabel: { shrink: pfi?.Product_Id ? true : false }
                          }}
                        />
                      </Grid>

                      <Grid size = {{ xs: selectedTab === "Deal Header" ? 6 : 3 }} className = "form-text-field">
                        <TextField
                          label = "Shipment Instructions"
                          id = "Shipment_Instructions"
                          value = {pfi?.SI_Id?.value || ""}
                          disabled
                          slotProps = {{
                            inputLabel: { shrink: pfi?.Product_Id ? true : false }
                          }}
                        />
                      </Grid>

                      {pfi?.Product_Grades?.length > 0 ? (
                        <Grid size = {{ xs: 12 }}>
                          <hr />

                          <div className = "d-flex justify-content-between align-items-center mb-2">
                            <h4 className = "sub-heading mb-0">Product Grades</h4>

                            <IconButton
                              color = "primary"
                              disabled = {!isWritable || isPeriodLocked}
                              onClick = {() => handleAddWrapper("Product Grade", pfiIndex)}
                            >
                              <AddBoxIcon 
                                fontSize = "large"
                                sx = {{ color: '#0C969C' }}
                              />
                            </IconButton>
                          </div>

                          {pfi?.Product_Grade_Id?.map((pfiProductGrade, pfiProductGradeIndex) => {
                            const pfiProductGradeIdSet = new Set(pfi?.Product_Grade_Id?.map(pfiProductGrade => pfiProductGrade?.Grade_Id?.id) || []);
                            const productGradeValue = pfiProductGrade?.Grade_Id?.value || `Product Grade ${pfiProductGradeIndex + 1}`;
                          
                            return (
                              <Grid key = {pfiProductGradeIndex}>
                                <Accordion
                                  ref = {(element) => (productGradeAccordionRefs.current[pfiProductGradeIndex] = element)}
                                  expanded = {isProductGradeAccordionExpanded === pfiProductGradeIndex}
                                  component = "div"
                                  onChange = {handleAccordionChange(pfiProductGradeIndex, "Product Grade")}
                                >
                                  <AccordionSummary
                                    expandIcon = {<ExpandMoreIcon />}
                                    aria-controls = {`product-grade-content-${pfiProductGradeIndex}`}
                                    id = {`product-grade-header-${pfiProductGradeIndex}`}
                                  >
                                    <small className = "page-heading fw-bold item">
                                      {productGradeValue}
                                    </small>

                                    <IconButton
                                      color = "error"
                                      size = "small"
                                      className = "pt-0"
                                      disabled = {!isWritable || isPeriodLocked}
                                      onClick = {(event) => handleDeleteProductGrade(event, pfiIndex, pfiProductGradeIndex)}
                                    >
                                      <DeleteIcon fontSize = "medium" />
                                    </IconButton>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Grid container spacing = {2} className = "w-100">
                                      <Grid size = {{ xs: selectedTab === "Deal Header" ? 6 : 3 }}>
                                        <VirtualizedAutocomplete
                                          isMultiple = {false}
                                          isObject = {true}
                                          isRequired = {true}
                                          isWritable = {isWritable}
                                          isLocked = {(Boolean(isPeriodLocked && isFieldLocked("Grade_Id", subTabName, tabName, lockedFields)))}
                                          filterOn = "Product Grade"
                                          options = {pfi?.Product_Grades?.filter(productGrade => !pfiProductGradeIdSet.has(productGrade.id)) || []}
                                          selectedOptions = {pfiProductGrade?.Grade_Id || null}
                                          handleSelectChange = {(filterOn, newValue) => handleSelectProductGrade(pfiIndex, pfiProductGradeIndex, newValue)}
                                        />
                                      </Grid>

                                      <Grid size = {{ xs: selectedTab === "Deal Header" ? 6 : 3 }} className = "form-text-field">
                                        <TextField
                                          label = {`${selectedTab === "Shipment Details" ? "Shipped " : ""}Quantity`}
                                          id = "Quantity"
                                          disabled = {(Boolean(isPeriodLocked && isFieldLocked("Quantity", subTabName, tabName, lockedFields)))}
                                          type = "number"
                                          value = {pfiProductGrade?.Quantity || ""}
                                          onChange = {(event) => handleProductGradeChange(pfiIndex, pfiProductGradeIndex, "Quantity", event.target.value)}
                                          size = "small"
                                          slotProps={{
                                            input: {
                                              readOnly: !isWritable,
                                              min: 0, 
                                              max: 2147483647, 
                                              step: "0.001",
                                              endAdornment: (
                                                <InputAdornment position="end" sx = {{marginRight:"10px"}}>
                                                  {Boolean(isPeriodLocked && isFieldLocked("Quantity", subTabName, tabName, lockedFields)) && (
                                                    <Tooltip
                                                      title="This field is locked and cannot be edited due to period lock."
                                                      arrow
                                                      placement="top"
                                                    >
                                                      <InfoIcon
                                                        fontSize="small"
                                                        sx={{ cursor: 'default'}} // No margins
                                                      />
                                                    </Tooltip>
                                                  )}
                                                </InputAdornment>
                                              ),
                                            },
                                          }}
                                          onKeyDown = {(event) => 
                                          {
                                            const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                                            if (invalidKeys.includes(event.key)) {
                                                event.preventDefault();
                                            }
                                          }}
                                        />
                                      </Grid>

                                      {selectedTab === "Deal Header" && (
                                        <>
                                          <Grid size = {{ xs: 6 }} className = "form-text-field">
                                           <TextField
                                              label = "Selling Price"
                                              id = "Selling_Price"
                                              disabled = {(Boolean(isPeriodLocked && isFieldLocked("Selling_Price", subTabName, tabName, lockedFields)))}
                                              type = "number"
                                              value = {pfiProductGrade?.Selling_Price || ""}
                                              onChange = {(event) => handleProductGradeChange(pfiIndex, pfiProductGradeIndex, "Selling_Price", event.target.value)}
                                              size = "small"
                                              slotProps={{
                                                input: {
                                                  readOnly: !isWritable,
                                                  min: 0, 
                                                  max: 2147483647, 
                                                  step: "0.001",
                                                  endAdornment: (
                                                    <InputAdornment position="end" sx = {{marginRight:"10px"}}>
                                                      {Boolean(isPeriodLocked && isFieldLocked("Selling_Price", subTabName, tabName, lockedFields)) && (
                                                        <Tooltip
                                                          title="This field is locked and cannot be edited due to period lock."
                                                          arrow
                                                          placement="top"
                                                        >
                                                          <InfoIcon
                                                            fontSize="small"
                                                            sx={{ cursor: 'default'}}
                                                          />
                                                        </Tooltip>
                                                      )}
                                                    </InputAdornment>
                                                  ),
                                                },
                                              }}
                                              onKeyDown = {(event) => 
                                              {
                                                const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                                                if (invalidKeys.includes(event.key)) {
                                                    event.preventDefault();
                                                }
                                              }}
                                            />
                                          </Grid>

                                         <TextField
                                            label = "Buying Price"
                                            id = "Buying_Price"
                                            type = "number"
                                            disabled
                                            value = {pfiProductGrade?.Buying_Price || ""}
                                            slotProps = {{ 
                                              inputLabel: { 
                                                shrink: pfiProductGrade?.Buying_Price ? true : false 
                                              },
                                              input: {
                                                min: 0, 
                                                max: 2147483647, 
                                                step: "0.001"
                                              }
                                            }}
                                          />

                                          <Grid size = {{ xs: 6 }} className = "form-text-field">
                                            <TextField
                                              label = "HS Code"
                                              id = "HS_Code"
                                              disabled = {(Boolean(isPeriodLocked && isFieldLocked("HS_Code", subTabName, tabName, lockedFields)))}
                                              name = "HS_Code"
                                              value = {pfiProductGrade?.HS_Code ? pfiProductGrade?.HS_Code?.toString() : ""}
                                              onChange = {(event) => handleProductGradeChange(pfiIndex, pfiProductGradeIndex, "HS_Code", event.target.value)}
                                              slotProps = {{ 
                                                input: { 
                                                  readOnly: !isWritable,
                                                  inputComponent: CustomTextMask 
                                                } 
                                              }}
                                            />
                                          </Grid>

                                          <Grid size = {{ xs: 12 }} className = "form-text-field">
                                            <TextField
                                              label = "Specification"
                                              id = "Specification"
                                              disabled = {(Boolean(isPeriodLocked && isFieldLocked("Specification", subTabName, tabName, lockedFields)))}
                                              multiline
                                              rows = {3}
                                              value = {pfiProductGrade?.Specification || ""}
                                              onChange = {(event) => handleProductGradeChange(pfiIndex, pfiProductGradeIndex, "Specification", event.target.value)}
                                              slotProps = {{
                                                input: {
                                                  readOnly: !isWritable
                                                }
                                              }}
                                            />
                                          </Grid>

                                          <Grid size = {{ xs: 12 }} className = "form-text-field">
                                            <TextField
                                              label = "Description"
                                              id = "Description"
                                              disabled = {(Boolean(isPeriodLocked && isFieldLocked("Description", subTabName, tabName, lockedFields)))}
                                              multiline
                                              rows = {3}
                                              value = {pfiProductGrade?.Description || ""}
                                              onChange = {(event) => handleProductGradeChange(pfiIndex, pfiProductGradeIndex, "Description", event.target.value)}
                                              slotProps = {{
                                                input: {
                                                  readOnly: !isWritable
                                                }
                                              }}
                                            />
                                          </Grid>
                                        </>
                                      )}
                                    </Grid>
                                  </AccordionDetails>
                                </Accordion>
                              </Grid>
                            );
                          })}
                        </Grid>
                      ) : (pfi?.PFI_Number && !isSelectedPFIDataLoading) && (
                        <small className = "w-100 mt-3">No product grades found against selected PFI.</small>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            );
          })}
        </Grid>
      ) : (sblcFormData?.SBLC_Id && !isPFIListLoading) && (
        <small>No PFIs found against selected SBLC.</small>
      )}

      <Dialog open = {showWarningDialog} onClose = {handleCloseDialog}>
        <DialogContent>
          <DialogContentText className = "text-center">
            Please fill in at least one field in the current SBLC details
            before adding a new one.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <button
            type = "button"
            variant = "contained"
            className = "btn btn-primary custom-btn"
            onClick = {handleCloseDialog}
          >
            Ok
          </button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default DealSBLC;